import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { LABEL_STOCK } from '../lib/assets';
import '../components/Assets.css';

/**
 * Printable QR labels for a place and every place beneath it: a storage
 * area's cupboards and shelves (104), a floor's rooms and desks, a room's
 * desks (door labels for the stock take, 29 October).
 *
 * Sized and printed like the device labels (AssetLabels). Each carries the
 * name large, the way down from the area in small type, and a QR holding
 * <this address>/assets/place/<id>, which opens that place's stocktake.
 */
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const A4 = { width: 210, height: 297 };

export default function AssetPlaceLabels() {
  const { locationId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [stockId, setStockId] = useState(() => {
    try { return localStorage.getItem('jarvis-label-stock') || 'L7160'; } catch { return 'L7160'; }
  });
  const [skip, setSkip] = useState(0);

  useEffect(() => {
    axios.get(`${API_BASE}/assets/places/${locationId}/labels`, { params: { base: window.location.origin } })
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.error || 'Could not make the labels.'));
  }, [locationId]);

  const stocks = LABEL_STOCK;
  const stock = stocks.find((s) => s.id === stockId) || stocks[0];
  const choose = (id) => {
    setStockId(id);
    try { localStorage.setItem('jarvis-label-stock', id); } catch { /* a private window */ }
  };

  const page = stock.roll ? { width: stock.width, height: stock.height } : A4;
  const perPage = stock.columns * stock.rows;
  const slots = useMemo(() => [
    ...Array(stock.roll ? 0 : Math.min(skip, perPage - 1)).fill(null), ...(data?.labels || [])
  ], [stock, skip, perPage, data]);
  const pages = [];
  for (let i = 0; i < slots.length; i += perPage) pages.push(slots.slice(i, i + perPage));

  const qr = Math.max(10, Math.min(stock.height - 3, stock.width * 0.45));
  const namePt = Math.max(6, Math.min(14, stock.height * 0.3));
  const subPt = Math.max(5, Math.min(9, stock.height * 0.2));

  return (
    <div className="as-print">
      <style>{`@page { size: ${page.width}mm ${page.height}mm; margin: 0; }`}</style>
      <div className="as-print-controls container">
        <p><Link to="/assets">← Assets</Link></p>
        <h1 className="page-title">
          {['storage', 'container', 'shelf'].includes(data?.place?.kind) ? 'Print shelf labels' : 'Print place labels'}
          {data?.place ? ` · ${data.place.name}` : ''}
        </h1>
        {error && <p className="as-error" role="alert">{error}</p>}
        {data && (
          <>
            <p className="as-quiet">
              {`${data.labels.length} label${data.labels.length === 1 ? '' : 's'}.`}
              {' '}Print at 100% (actual size), with headers and footers switched off. A phone camera opens the stock take for that place; scanned during a stock take, a label says which room you are in.
            </p>
            <div className="as-inline-form">
              <div className="form-group">
                <label htmlFor="as-stock">Label stock</label>
                <select id="as-stock" value={stock.id} onChange={(e) => choose(e.target.value)}>
                  {stocks.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              {!stock.roll && (
                <div className="form-group" style={{ flex: '0 1 9rem' }}>
                  <label htmlFor="as-skip">Start at label</label>
                  <input id="as-skip" type="number" min={1} max={perPage} value={skip + 1}
                    onChange={(e) => setSkip(Math.max(0, Math.min(perPage - 1, (Number(e.target.value) || 1) - 1)))} />
                </div>
              )}
              <button type="button" className="btn btn-primary" onClick={() => window.print()}
                disabled={!data.labels.length}>
                Print
              </button>
            </div>
          </>
        )}
      </div>

      <div className="as-sheets">
        {pages.map((labels, p) => (
          <div key={p} className="as-sheet" data-roll={String(Boolean(stock.roll))}
            style={{ width: `${page.width}mm`, height: `${page.height}mm` }}>
            {labels.map((l, i) => {
              const col = i % stock.columns;
              const row = Math.floor(i / stock.columns);
              const style = {
                left: `${stock.left + col * (stock.width + stock.gap_x)}mm`,
                top: `${stock.top + row * (stock.height + stock.gap_y)}mm`,
                width: `${stock.width}mm`,
                height: `${stock.height}mm`
              };
              if (!l) return <div key={`skip-${i}`} className="as-label" data-blank="true" style={style} />;
              return (
                <div key={l.id} className="as-label" data-testid="place-label" style={style}>
                  <div className="as-label-qr" style={{ width: `${qr}mm`, height: `${qr}mm` }}
                    // Made by the server's qrcode library from this
                    // application's address and the place's id; nothing typed
                    // is embedded as markup.
                    dangerouslySetInnerHTML={{ __html: l.svg }} />
                  <div className="as-label-text">
                    <div className="as-label-tag" style={{ fontSize: `${namePt}pt` }}>{l.name}</div>
                    {l.path_below && l.path_below !== l.name && (
                      <div className="as-label-sub" style={{ fontSize: `${subPt}pt` }}>{l.path_below}</div>
                    )}
                    <div className="as-label-sub" style={{ fontSize: `${Math.max(5, subPt - 1)}pt` }}>
                      {data.place.name} · scan to count
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
